import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { DashboardLayout } from '../components/Layout';

const AdminTokenShopPage = () => {
    const [activeTab, setActiveTab] = useState('wallet');
    const [wallet, setWallet] = useState(null);
    const [packages, setPackages] = useState([]);
    const [purchaseHistory, setPurchaseHistory] = useState([]);
    const [clientBalances, setClientBalances] = useState([]);
    const [distributions, setDistributions] = useState([]);
    const [pendingPurchases, setPendingPurchases] = useState([]);
    const [allPurchases, setAllPurchases] = useState([]);
    const [shopStats, setShopStats] = useState(null);
    const [aiCosts, setAiCosts] = useState([]);
    const [loading, setLoading] = useState(true);
    // Distribute modal
    const [showDistributeModal, setShowDistributeModal] = useState(false);
    const [distributeUserId, setDistributeUserId] = useState('');
    const [distributeAmount, setDistributeAmount] = useState('');
    const [distributeNotes, setDistributeNotes] = useState('');
    const [users, setUsers] = useState([]);
    const [buyingPackage, setBuyingPackage] = useState(null);
    // Remove modal
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [removeUserId, setRemoveUserId] = useState('');
    const [removeUserName, setRemoveUserName] = useState('');
    const [removeUserBalance, setRemoveUserBalance] = useState(0);
    const [removeAmount, setRemoveAmount] = useState('');
    const [removeNotes, setRemoveNotes] = useState('');
    // Search filter for client balances
    const [clientSearch, setClientSearch] = useState('');

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [walletRes, pkgRes, histRes, clientsRes, distRes, pendingRes, allRes, statsRes, costsRes, usersRes] = await Promise.all([
                api.get('/TokenShop/admin/wallet').catch(() => ({ data: {} })),
                api.get('/TokenShop/admin/packages').catch(() => ({ data: [] })),
                api.get('/TokenShop/admin/purchase-history').catch(() => ({ data: [] })),
                api.get('/TokenShop/admin/client-balances').catch(() => ({ data: [] })),
                api.get('/TokenShop/admin/distributions').catch(() => ({ data: [] })),
                api.get('/TokenShop/admin/purchases/pending').catch(() => ({ data: [] })),
                api.get('/TokenShop/admin/purchases').catch(() => ({ data: [] })),
                api.get('/TokenShop/admin/stats').catch(() => ({ data: {} })),
                api.get('/TokenShop/ai-costs').catch(() => ({ data: [] })),
                api.get('/users').catch(() => ({ data: [] })),
            ]);
            setWallet(walletRes.data);
            setPackages(Array.isArray(pkgRes.data) ? pkgRes.data : []);
            setPurchaseHistory(Array.isArray(histRes.data) ? histRes.data : []);
            setClientBalances(Array.isArray(clientsRes.data) ? clientsRes.data : []);
            setDistributions(Array.isArray(distRes.data) ? distRes.data : []);
            setPendingPurchases(Array.isArray(pendingRes.data) ? pendingRes.data : []);
            setAllPurchases(Array.isArray(allRes.data) ? allRes.data : []);
            setShopStats(statsRes.data);
            setAiCosts(Array.isArray(costsRes.data) ? costsRes.data : []);
            setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        } catch (error) {
            console.error('Erro:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBuyPackage = async (pkg) => {
        if (!window.confirm(`Comprar "${pkg.name}" por €${pkg.price?.toFixed(2)}? Receberá ${pkg.tokenAmount} tokens na carteira.`)) return;
        setBuyingPackage(pkg.id);
        try {
            const res = await api.post(`/TokenShop/admin/buy-package/${pkg.id}`);
            toast.success(res.data.message);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erro ao comprar pacote');
        } finally {
            setBuyingPackage(null);
        }
    };

    const handleDistribute = async () => {
        if (!distributeUserId || !distributeAmount || distributeAmount <= 0) {
            toast.error('Preencha o utilizador e a quantidade');
            return;
        }
        try {
            const res = await api.post('/TokenShop/admin/distribute', {
                userId: parseInt(distributeUserId),
                amount: parseInt(distributeAmount),
                type: 'manual',
                notes: distributeNotes || null
            });
            toast.success(res.data.message);
            setShowDistributeModal(false);
            setDistributeUserId('');
            setDistributeAmount('');
            setDistributeNotes('');
            loadData();
        } catch (error) {
            toast.error(error.response?.data || 'Erro ao distribuir tokens');
        }
    };

    const handleRemoveTokens = async () => {
        if (!removeUserId || !removeAmount || removeAmount <= 0) {
            toast.error('Preencha a quantidade');
            return;
        }
        try {
            const res = await api.post('/TokenShop/admin/remove-tokens', {
                userId: parseInt(removeUserId),
                amount: parseInt(removeAmount),
                notes: removeNotes || null
            });
            toast.success(res.data.message);
            setShowRemoveModal(false);
            setRemoveUserId('');
            setRemoveAmount('');
            setRemoveNotes('');
            setRemoveUserName('');
            setRemoveUserBalance(0);
            loadData();
        } catch (error) {
            toast.error(error.response?.data || 'Erro ao remover tokens');
        }
    };

    const openRemoveModal = (client) => {
        setRemoveUserId(client.userId.toString());
        setRemoveUserName(client.userName);
        setRemoveUserBalance(client.balance);
        setRemoveAmount('');
        setRemoveNotes('');
        setShowRemoveModal(true);
    };

    const filteredClientBalances = clientBalances.filter(c => {
        if (!clientSearch) return true;
        const s = clientSearch.toLowerCase();
        return c.userName?.toLowerCase().includes(s) || c.userEmail?.toLowerCase().includes(s);
    });

    const handleApprove = async (id) => {
        if (!window.confirm('Aprovar esta compra e debitar tokens ao cliente?')) return;
        try {
            const res = await api.post(`/TokenShop/admin/purchases/${id}/approve`);
            toast.success(res.data.message || 'Compra aprovada!');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erro ao aprovar');
        }
    };

    const handleReject = async (id) => {
        if (!window.confirm('Rejeitar esta compra?')) return;
        try {
            await api.post(`/TokenShop/admin/purchases/${id}/reject`);
            toast.success('Compra rejeitada');
            loadData();
        } catch (error) {
            toast.error('Erro ao rejeitar');
        }
    };

    const handleUpdateAiCost = async (feature, newCost) => {
        try {
            await api.put('/TokenShop/admin/ai-costs', [{ feature, tokenCost: parseInt(newCost) }]);
            toast.success('Custo atualizado!');
            loadData();
        } catch (e) {
            toast.error('Erro ao atualizar');
        }
    };

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending': return { bg: 'var(--warning-bg)', color: 'var(--warning)', label: '⏳ Pendente' };
            case 'completed': return { bg: 'var(--success-bg)', color: 'var(--success-text)', label: '✅ Aprovado' };
            case 'rejected': return { bg: 'var(--error-bg)', color: 'var(--error)', label: '❌ Rejeitado' };
            default: return { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)', label: status };
        }
    };

    if (loading) {
        return (
            <DashboardLayout userType="admin">
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <div className="spinner"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userType="admin">
            <div style={styles.container}>
                <div style={styles.header}>
                    <div>
                        <h1 style={styles.title}>🪙 Créditos & Tokens - Admin</h1>
                        <p style={styles.subtitle}>Gestão de carteira, distribuição e loja</p>
                    </div>
                    {pendingPurchases.length > 0 && (
                        <div style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--warning-bg)', borderRadius: '12px', fontSize: '0.875rem', fontWeight: '600', color: 'var(--warning)' }}>
                            ⏳ {pendingPurchases.length} compras pendentes
                        </div>
                    )}
                </div>

                {/* Wallet KPIs */}
                <div style={styles.kpiGrid}>
                    <div style={{ ...styles.kpiCard, background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', color: 'white' }}>
                        <span style={{ fontSize: '2.5rem' }}>🪙</span>
                        <div>
                            <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Carteira do Ginásio</div>
                            <div style={{ fontSize: '2rem', fontWeight: '800' }}>{wallet?.balance || 0}</div>
                            <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>tokens disponíveis</div>
                        </div>
                    </div>
                    <div style={styles.kpiCard}>
                        <span style={{ fontSize: '2rem' }}>📦</span>
                        <div>
                            <div style={styles.kpiLabel}>Total Comprado</div>
                            <div style={styles.kpiValue}>{wallet?.totalPurchased || 0}</div>
                        </div>
                    </div>
                    <div style={styles.kpiCard}>
                        <span style={{ fontSize: '2rem' }}>🎁</span>
                        <div>
                            <div style={styles.kpiLabel}>Total Distribuído</div>
                            <div style={styles.kpiValue}>{wallet?.totalDistributed || 0}</div>
                        </div>
                    </div>
                    <div style={styles.kpiCard}>
                        <span style={{ fontSize: '2rem' }}>👥</span>
                        <div>
                            <div style={styles.kpiLabel}>Tokens dos Clientes</div>
                            <div style={{ ...styles.kpiValue, color: 'var(--accent-primary)' }}>{wallet?.totalClientTokens || 0}</div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div style={styles.tabs}>
                    {[
                        { id: 'wallet', label: '🪙 Carteira' },
                        { id: 'distribute', label: '🎁 Distribuir' },
                        { id: 'pending', label: `⏳ Pendentes (${pendingPurchases.length})` },
                        { id: 'purchases', label: '📋 Compras Loja' },
                        { id: 'ai-costs', label: '🤖 Custos IA' },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* WALLET TAB */}
                {activeTab === 'wallet' && (
                    <div>
                        <h3 style={{ marginBottom: '1rem' }}>📦 Comprar Tokens ao SuperUser</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                            {packages.map(pkg => (
                                <div key={pkg.id} style={{ ...styles.card, textAlign: 'center', position: 'relative', border: pkg.isPopular ? '2px solid var(--warning)' : '1px solid var(--border-color)' }}>
                                    {pkg.isPopular && <div style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', background: 'var(--warning)', color: 'white', padding: '2px 12px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '700' }}>⭐ MAIS POPULAR</div>}
                                    <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>{pkg.icon || '📦'}</div>
                                    <div style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '4px' }}>{pkg.name}</div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>{pkg.description}</div>
                                    <div style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--warning)', marginBottom: '4px' }}>{pkg.tokenAmount} 🪙</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--accent-primary)', marginBottom: '16px' }}>€{pkg.price?.toFixed(2)}</div>
                                    <button
                                        style={{ ...styles.buyBtn, opacity: buyingPackage === pkg.id ? 0.6 : 1 }}
                                        disabled={buyingPackage === pkg.id}
                                        onClick={() => handleBuyPackage(pkg)}
                                    >
                                        {buyingPackage === pkg.id ? 'A comprar...' : '🛒 Comprar Pack'}
                                    </button>
                                </div>
                            ))}
                        </div>

                        <h3 style={{ marginBottom: '1rem' }}>📜 Histórico de Compras</h3>
                        <div style={styles.card}>
                            {purchaseHistory.length === 0 ? (
                                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>Sem compras ainda</p>
                            ) : (
                                <table style={styles.table} className="adm-table">
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Data</th>
                                            <th style={styles.th}>Pacote</th>
                                            <th style={styles.th}>Tokens</th>
                                            <th style={styles.th}>Preço</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {purchaseHistory.map(p => (
                                            <tr key={p.id}>
                                                <td style={styles.td} data-label="Data">{new Date(p.purchasedAt).toLocaleDateString('pt-PT')}</td>
                                                <td style={styles.td} data-label="Pacote">{p.packageName}</td>
                                                <td style={{ ...styles.td, fontWeight: '700', color: 'var(--warning)' }} data-label="Tokens">+{p.tokenAmount} 🪙</td>
                                                <td style={styles.td} data-label="Preço">€{p.pricePaid?.toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {/* DISTRIBUTE TAB */}
                {activeTab === 'distribute' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3>🎁 Distribuir Tokens aos Clientes</h3>
                            <button style={styles.buyBtn} onClick={() => setShowDistributeModal(true)}>
                                ➕ Atribuir Tokens
                            </button>
                        </div>

                        <div style={{ ...styles.card, marginBottom: '1.5rem', backgroundColor: wallet?.balance < 100 ? '#fef2f2' : 'white' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Saldo disponível para distribuir:</div>
                                    <div style={{ fontSize: '2rem', fontWeight: '800', color: wallet?.balance < 100 ? '#dc2626' : '#f59e0b' }}>{wallet?.balance || 0} 🪙</div>
                                </div>
                                {wallet?.balance < 100 && (
                                    <div style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--warning-bg)', borderRadius: '12px', fontSize: '0.8rem', color: 'var(--warning)', fontWeight: '600' }}>
                                        ⚠️ Saldo baixo! Compre mais tokens.
                                    </div>
                                )}
                            </div>
                        </div>

                        <h4 style={{ marginBottom: '0.75rem' }}>👥 Saldos dos Clientes</h4>
                        <div style={{ marginBottom: '0.75rem' }}>
                            <input
                                type="text"
                                placeholder="🔍 Pesquisar por nome ou email..."
                                value={clientSearch}
                                onChange={e => setClientSearch(e.target.value)}
                                style={{ width: '100%', padding: '0.625rem 1rem', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.875rem', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div style={styles.card}>
                            {filteredClientBalances.length === 0 ? (
                                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>{clientSearch ? 'Nenhum resultado encontrado' : 'Sem utilizadores com tokens'}</p>
                            ) : (
                                <table style={styles.table} className="adm-table">
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Utilizador</th>
                                            <th style={styles.th}>Email</th>
                                            <th style={styles.th}>Saldo</th>
                                            <th style={styles.th}>Ganhos</th>
                                            <th style={styles.th}>Gastos</th>
                                            <th style={styles.th}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredClientBalances.map(c => (
                                            <tr key={c.userId}>
                                                <td style={styles.td}><strong>{c.userName}</strong></td>
                                                <td style={styles.td} data-label="Email">{c.userEmail}</td>
                                                <td style={{ ...styles.td, fontWeight: '700', color: 'var(--warning)' }} data-label="Saldo">{c.balance} 🪙</td>
                                                <td style={styles.td} data-label="Ganhos">{c.totalEarned}</td>
                                                <td style={styles.td} data-label="Gastos">{c.totalSpent}</td>
                                                <td style={styles.td} className="adm-actions">
                                                    <div style={{ display: 'flex', gap: '0.375rem' }}>
                                                        <button
                                                            style={{ ...styles.smallBtn }}
                                                            onClick={() => { setDistributeUserId(c.userId.toString()); setShowDistributeModal(true); }}
                                                        >
                                                            ➕ Dar
                                                        </button>
                                                        <button
                                                            style={{ ...styles.smallBtnRed }}
                                                            onClick={() => openRemoveModal(c)}
                                                        >
                                                            ➖ Tirar
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem' }}>📜 Histórico de Distribuições</h4>
                        <div style={styles.card}>
                            {distributions.length === 0 ? (
                                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>Sem distribuições</p>
                            ) : (
                                <table style={styles.table} className="adm-table">
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Data</th>
                                            <th style={styles.th}>Utilizador</th>
                                            <th style={styles.th}>Tokens</th>
                                            <th style={styles.th}>Tipo</th>
                                            <th style={styles.th}>Notas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {distributions.map(d => (
                                            <tr key={d.id}>
                                                <td style={styles.td} data-label="Data">{new Date(d.createdAt).toLocaleDateString('pt-PT')}</td>
                                                <td style={styles.td} data-label="Utilizador">{d.userName}</td>
                                                <td style={{ ...styles.td, fontWeight: '700', color: 'var(--success-text)' }} data-label="Tokens">+{d.amount} 🪙</td>
                                                <td style={styles.td} data-label="Tipo">{d.type}</td>
                                                <td style={styles.td} data-label="Notas">{d.notes || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {/* PENDING TAB */}
                {activeTab === 'pending' && (
                    <div>
                        {pendingPurchases.length === 0 ? (
                            <div style={styles.emptyState}>
                                <span style={{ fontSize: '3rem' }}>✅</span>
                                <h3>Sem compras pendentes</h3>
                                <p>Todas as compras na loja foram processadas.</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {pendingPurchases.map(p => (
                                    <div key={p.id} style={styles.purchaseRow}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <strong>{p.itemName}</strong>
                                                <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                                    👤 {p.userName} ({p.userEmail})
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--warning)' }}>🪙 {p.tokensSpent}</div>
                                                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                                                    {new Date(p.purchasedAt).toLocaleDateString('pt-PT')}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                                            <button style={styles.approveBtn} onClick={() => handleApprove(p.id)}>✅ Aprovar</button>
                                            <button style={styles.rejectBtn} onClick={() => handleReject(p.id)}>❌ Rejeitar</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ALL PURCHASES TAB */}
                {activeTab === 'purchases' && (
                    <div style={styles.card}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={styles.table} className="adm-table">
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Data</th>
                                        <th style={styles.th}>Utilizador</th>
                                        <th style={styles.th}>Item</th>
                                        <th style={styles.th}>Tokens</th>
                                        <th style={styles.th}>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allPurchases.length === 0 ? (
                                        <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Sem compras</td></tr>
                                    ) : allPurchases.map(p => {
                                        const statusInfo = getStatusBadge(p.status);
                                        return (
                                            <tr key={p.id}>
                                                <td style={styles.td} data-label="Data">{new Date(p.purchasedAt).toLocaleDateString('pt-PT')}</td>
                                                <td style={styles.td} data-label="Utilizador">{p.userName}</td>
                                                <td style={styles.td} data-label="Item">{p.itemIcon} {p.itemName}</td>
                                                <td style={{ ...styles.td, fontWeight: '600', color: 'var(--warning)' }} data-label="Tokens">🪙 {p.tokensSpent}</td>
                                                <td style={styles.td} data-label="Estado">
                                                    <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', backgroundColor: statusInfo.bg, color: statusInfo.color }}>{statusInfo.label}</span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* AI COSTS TAB */}
                {activeTab === 'ai-costs' && (
                    <div>
                        <h3 style={{ marginBottom: '1rem' }}>🤖 Custos de Tokens por Funcionalidade IA</h3>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                            Defina quantos tokens cada utilização de IA consome ao cliente.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {aiCosts.map(c => (
                                <div key={c.id} style={{ ...styles.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <div style={{ fontWeight: '600' }}>{c.featureLabel}</div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Feature: {c.feature}</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                            type="number"
                                            min="0"
                                            defaultValue={c.tokenCost}
                                            style={{ width: '60px', padding: '6px 8px', borderRadius: '8px', border: '1px solid var(--border-color)', textAlign: 'center', fontWeight: '700', fontSize: '1rem' }}
                                            onBlur={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (val !== c.tokenCost && val >= 0) handleUpdateAiCost(c.feature, val);
                                            }}
                                        />
                                        <span style={{ fontSize: '1.25rem' }}>🪙</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Distribute Modal */}
            {showDistributeModal && (
                <div style={styles.modalOverlay} onClick={() => setShowDistributeModal(false)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <h3 style={{ marginBottom: '1rem' }}>🎁 Atribuir Tokens a Cliente</h3>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                            Saldo da carteira: <strong style={{ color: 'var(--warning)' }}>{wallet?.balance || 0} 🪙</strong>
                        </p>
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={styles.label}>Utilizador</label>
                            <select
                                style={styles.input}
                                value={distributeUserId}
                                onChange={e => setDistributeUserId(e.target.value)}
                            >
                                <option value="">Selecione...</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={styles.label}>Quantidade de Tokens</label>
                            <input
                                type="number"
                                min="1"
                                max={wallet?.balance || 0}
                                style={styles.input}
                                value={distributeAmount}
                                onChange={e => setDistributeAmount(e.target.value)}
                                placeholder="Ex: 50"
                            />
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={styles.label}>Notas (opcional)</label>
                            <input
                                style={styles.input}
                                value={distributeNotes}
                                onChange={e => setDistributeNotes(e.target.value)}
                                placeholder="Motivo da atribuição..."
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <button style={{ ...styles.rejectBtn, flex: 1 }} onClick={() => setShowDistributeModal(false)}>Cancelar</button>
                            <button style={{ ...styles.buyBtn, flex: 1 }} onClick={handleDistribute}>🎁 Atribuir Tokens</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Tokens Modal */}
            {showRemoveModal && (
                <div style={styles.modalOverlay} onClick={() => setShowRemoveModal(false)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <h3 style={{ marginBottom: '1rem', color: 'var(--error)' }}>➖ Remover Tokens</h3>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                            Utilizador: <strong>{removeUserName}</strong>
                        </p>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                            Saldo atual: <strong style={{ color: 'var(--warning)' }}>{removeUserBalance} 🪙</strong>
                        </p>
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={styles.label}>Quantidade a Remover</label>
                            <input
                                type="number"
                                min="1"
                                max={removeUserBalance}
                                style={styles.input}
                                value={removeAmount}
                                onChange={e => setRemoveAmount(e.target.value)}
                                placeholder="Ex: 50"
                            />
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={styles.label}>Motivo (opcional)</label>
                            <input
                                style={styles.input}
                                value={removeNotes}
                                onChange={e => setRemoveNotes(e.target.value)}
                                placeholder="Motivo da remoção..."
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <button style={{ ...styles.rejectBtn, flex: 1 }} onClick={() => setShowRemoveModal(false)}>Cancelar</button>
                            <button style={{ ...styles.removeBtnLg, flex: 1 }} onClick={handleRemoveTokens}>➖ Remover Tokens</button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

const styles = {
    container: { padding: '1rem' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
    title: { margin: 0, fontSize: '1.5rem' },
    subtitle: { margin: '0.25rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.875rem' },
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1rem' },
    kpiCard: { backgroundColor: 'white', borderRadius: '12px', padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    kpiLabel: { fontSize: '0.75rem', color: 'var(--text-secondary)' },
    kpiValue: { fontSize: '1.5rem', fontWeight: '700' },
    tabs: { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', flexWrap: 'wrap' },
    tab: { padding: '0.5rem 1rem', border: 'none', background: 'none', cursor: 'pointer', borderRadius: '8px', fontSize: '0.875rem', color: 'var(--text-secondary)' },
    tabActive: { backgroundColor: 'var(--warning)', color: 'white' },
    card: { backgroundColor: 'white', borderRadius: '12px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1rem' },
    purchaseRow: { backgroundColor: 'white', borderRadius: '12px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    buyBtn: { padding: '0.75rem 1.5rem', backgroundColor: 'var(--warning)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '0.875rem', width: '100%' },
    smallBtn: { padding: '0.375rem 0.75rem', backgroundColor: 'var(--warning)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem' },
    smallBtnRed: { padding: '0.375rem 0.75rem', backgroundColor: 'var(--error-bg)', color: 'var(--error)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem' },
    removeBtnLg: { padding: '0.75rem 1.5rem', backgroundColor: 'var(--error)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '0.875rem', width: '100%' },
    approveBtn: { flex: 1, padding: '0.625rem 1rem', backgroundColor: 'var(--success)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' },
    rejectBtn: { padding: '0.625rem 1rem', backgroundColor: 'var(--error-bg)', color: 'var(--error)', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' },
    emptyState: { textAlign: 'center', padding: '3rem', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
    th: { textAlign: 'left', padding: '0.625rem', borderBottom: '2px solid var(--border-color)', color: 'var(--text-secondary)', fontSize: '0.75rem', textTransform: 'uppercase' },
    td: { padding: '0.625rem', borderBottom: '1px solid var(--info)' },
    modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '16px', padding: '2rem', width: '90%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
    label: { display: 'block', fontSize: '0.8125rem', color: 'var(--info-text)', marginBottom: '4px', fontWeight: '600' },
    input: { width: '100%', padding: '0.625rem', borderRadius: '8px', border: '1px solid var(--border-color)', fontSize: '0.875rem', boxSizing: 'border-box' },
};

export default AdminTokenShopPage;
